/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import template from './tabs.stache';
import urlUtil from 'scripts/utils/url';
import can from 'can';
import dtm from 'scripts/services/dtm';
import appContext from 'scripts/app-context';

const kpis = [
    { value: 'driving-distance', title: 'Driving Distance' },
    { value: 'fairways-hit', title: 'Fairways Hit' },
    { value: 'greens-in-regulation', title: 'Greens in Regulation' },
    { value: 'putting', title: 'Putting' },
    { value: 'birdies', title: 'Birdies' },
    { value: 'eagles', title: 'Eagles' },
];

can.Component.extend({
    tag: 'history-experience-scoring-and-stats-tabs',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            years: {
                Type: can.List,
                Value: can.List,
            },
            kpis: {
                Type: can.List,
                Value: null,
            },
            kpi: {
                type: 'string',
            },
            yearSelected: {
                type: 'string',
            },
        },

        init() {
            if (this.attr('type') == 'history-experience-stats') {
                can.route.ready();
                this.attr('kpi', can.route.attr('s') || 'driving-distance');
                this.attr('kpis', kpis);
            }
        },

        handleRoutes(options) {
            let year, kpi;
            year =
                options.year ||
                urlUtil.getHashValue('y') ||
                this.attr('yearSelected');
            kpi = options.kpi || urlUtil.getHashValue('s') || this.attr('kpi');

            if (year) {
                this.attr('yearSelected', year);
            }
            if (kpi) {
                this.attr('kpi', kpi);
            }

            can.route.attr({
                y: year,
                s: kpi,
            });
        },
    },
    events: {
        inserted() {
            appContext.model.bind(
                'history-experience-scoring-and-stats-years-ready',
                this.fillYears.bind(this),
            );
            appContext.model.bind(
                'history-experience-stats-ready',
                this.handleStatsReady.bind(this),
            );
            appContext.model.bind(
                'history-experience-scoring-and-stats-year-remove',
                this.removeYear.bind(this),
            );

            this.viewModel.bind('yearSelected', (_, year) => {
                this.handleYearChange(year);
            });

            this.viewModel.bind('kpi', (_, kpi) => {
                this.handleKpiChange(kpi);
            });

            can.route.ready();
        },

        selectYear(year) {
            const yearList = this.viewModel.attr('years');

            if (!Boolean(yearList) || !Boolean(yearList.length)) {
                return;
            }

            const years = yearList.serialize();
            const yearEntry = Number(year);
            const yearIndex = years.indexOf(yearEntry);
            const selectedYear = yearIndex < 0 ? years[0] : years[yearIndex];

            this.viewModel.attr('yearSelected', selectedYear);
        },

        updateYear(year) {
            const yearChangeEvent =
                'history-experience-scoring-and-stats-years-change';
            appContext.model.dispatch(yearChangeEvent, [year]);
        },

        updateKpi(kpi) {
            const kpiChangeEvent = 'history-experience-stats-kpi-change';
            appContext.model.dispatch(kpiChangeEvent, [kpi]);
        },

        removeYear(_, yearToRemove) {
            const newYearsEntry = this.viewModel
                .attr('years')
                .serialize()
                .filter((year) => year != yearToRemove);
            this.fillYears(null, newYearsEntry);
        },

        fillYears(_, years, legend) {
            if (!Boolean(years) || !Boolean(years.length)) {
                return;
            }

            this.viewModel.attr('years', years);
            const selectedYear =
                urlUtil.getHashValue('y') ||
                this.viewModel.attr('yearSelected');

            can.batch.start();
            this.selectYear(selectedYear);
            can.batch.stop();
        },

        handleStatsReady(_) {
            appContext.model.dispatch('history-experience-stats-kpi-change', [
                this.viewModel.attr('kpi'),
            ]);
        },

        handleYearChange(year) {
            this.viewModel.handleRoutes({ year });
            dtm.trackHistoryTimelineYearSelection(year);
            this.updateYear(year);
        },

        handleKpiChange(kpi) {
            this.viewModel.handleRoutes({ kpi });
            dtm.trackHistoryTimelineTagSelection(kpi);
            this.updateKpi(kpi);
        },
    },
    helpers: {
        isActive(options) {
            if (!options.context.url) {
                return options.inverse();
            }

            const commonPath = options.context.url.split('.')[0];
            if (location.href.indexOf(commonPath) != -1) {
                return options.fn();
            }

            return options.inverse();
        },
    },
});
